import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Switch, FormControlLabel } from '@mui/material';
import { panelTheme } from '../PanelComerciales.theme';
import { FieldGroup } from '../PanelComerciales.styles';

export type FieldKind = 'text' | 'select' | 'switch' | 'number';

export interface FieldDef<T> {
    key: keyof T;
    label: string;
    kind: FieldKind;
    options?: string[];
    helper?: string;
    validator?: (v: unknown, all: T) => string | null;
}

interface EditRecordModalProps<T extends Record<string, unknown>> {
    open: boolean;
    title: string;
    fields: FieldDef<T>[];
    initial: T;
    onClose: () => void;
    onSave: (next: T) => void;
}

function EditRecordModal<T extends Record<string, unknown>>({
    open,
    title,
    fields,
    initial,
    onClose,
    onSave,
}: EditRecordModalProps<T>) {
    const [draft, setDraft] = useState<T>(initial);
    const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});

    useEffect(() => {
        if (open) {
            setDraft(initial);
            setErrors({});
        }
    }, [open, initial]);

    const setField = (key: keyof T, value: unknown) => {
        setDraft((prev) => ({ ...prev, [key]: value }));
    };

    const handleSubmit = () => {
        const nextErrors: Partial<Record<keyof T, string>> = {};
        fields.forEach((f) => {
            if (f.validator) {
                const e = f.validator(draft[f.key], draft);
                if (e) nextErrors[f.key] = e;
            }
        });
        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors);
            return;
        }
        onSave(draft);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle sx={{ fontWeight: 600, color: panelTheme.colors.text }}>{title}</DialogTitle>
            <DialogContent dividers>
                {fields.map((f) => (
                    <FieldGroup key={String(f.key)}>
                        <label>{f.label}</label>
                        {f.kind === 'text' && (
                            <TextField
                                size='small'
                                value={(draft[f.key] as string) ?? ''}
                                onChange={(e) => setField(f.key, e.target.value)}
                                error={Boolean(errors[f.key])}
                                helperText={errors[f.key] || f.helper}
                            />
                        )}
                        {f.kind === 'number' && (
                            <TextField
                                size='small'
                                type='number'
                                value={(draft[f.key] as number) ?? 0}
                                onChange={(e) => setField(f.key, Number(e.target.value))}
                                error={Boolean(errors[f.key])}
                                helperText={errors[f.key] || f.helper}
                            />
                        )}
                        {f.kind === 'select' && (
                            <TextField
                                size='small'
                                select
                                value={(draft[f.key] as string) ?? ''}
                                onChange={(e) => setField(f.key, e.target.value)}
                                error={Boolean(errors[f.key])}
                                helperText={errors[f.key] || f.helper}
                            >
                                {(f.options || []).map((opt) => (
                                    <MenuItem key={opt} value={opt}>
                                        {opt}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                        {f.kind === 'switch' && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={Boolean(draft[f.key])}
                                        onChange={(e) => setField(f.key, e.target.checked)}
                                    />
                                }
                                label={f.helper ?? ''}
                            />
                        )}
                    </FieldGroup>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>
                    Cancelar
                </Button>
                <Button
                    variant='contained'
                    onClick={handleSubmit}
                    sx={{
                        textTransform: 'none',
                        background: panelTheme.colors.indigo,
                        '&:hover': { background: panelTheme.colors.violet },
                    }}
                >
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default EditRecordModal;
